import Carousel from './carousel'
import { shopifyImageSrcset, shopifyImageUrl } from '../utils/shopify-image'

const SHOP_DOMAIN = 'shop.lausannehc.ch'
const COLLECTION_HANDLE = 'website'
const PRODUCTS_COUNT = 12

const COLLECTION_IMAGE_WIDTHS = [640, 960, 1320, 1800, 2640]

const PRODUCT_IMAGE_WIDTHS = [300, 450, 600, 800, 1000, 1400]

// Public, read-only Storefront API token — safe to expose client-side.
const STOREFRONT_TOKEN = '7bf999e29b526048328f478173fd6100'
const API_VERSION = '2026-07'

type Product = {
  id: string
  title: string
  url: string
  imageUrl: string | null
  imageSrcset: string | null
  imageAlt: string
  price: string
  compareAtPrice: string | null
  badge: string | null
}

type CollectionImage = {
  url: string
  srcset: string
  alt: string
} | null

type FetchResult = {
  image: CollectionImage
  products: Product[]
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('fr-CH', { style: 'currency', currency }).format(amount)
}

// Free-text badge (e.g. "Nouveau", "Remise de 50%") is opted into per-product via a Shopify tag
// prefixed with "badge:" — kept separate from regular filter tags. Only the first match is used.
const BADGE_TAG_PREFIX = 'badge:'

function extractBadge(tags: string[]): string | null {
  const badgeTag = tags.find((tag) => tag.toLowerCase().startsWith(BADGE_TAG_PREFIX))
  return badgeTag ? badgeTag.slice(BADGE_TAG_PREFIX.length).trim() : null
}

// Single price when all variants share the same price; "min - max" range otherwise (no
// strikethrough shown for ranges, too ambiguous which variant's compare-at applies).
// Strikethrough compare-at price only shown for a single price genuinely on sale.
function buildPriceDisplay(
  min: number,
  max: number,
  compareAtMin: number | null,
  currency: string
): { price: string; compareAtPrice: string | null } {
  if (min !== max) {
    return { price: `${ formatPrice(min, currency) }  –  ${ formatPrice(max, currency) }`, compareAtPrice: null }
  }

  return {
    price: formatPrice(min, currency),
    compareAtPrice: compareAtMin && compareAtMin > min ? formatPrice(compareAtMin, currency) : null,
  }
}

const STOREFRONT_QUERY = `
  query ProductsCarousel($handle: String!, $first: Int!) {
    collectionByHandle(handle: $handle) {
      image {
        url
        altText
      }
      products(first: $first) {
        nodes {
          id
          title
          handle
          tags
          featuredImage {
            url
            altText
            width
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
            }
          }
        }
      }
    }
  }
`

type StorefrontProductNode = {
  id: string
  title: string
  handle: string
  tags: string[]
  featuredImage: { url: string; altText: string | null; width: number } | null
  priceRange: { minVariantPrice: { amount: string; currencyCode: string }; maxVariantPrice: { amount: string } }
  compareAtPriceRange: { minVariantPrice: { amount: string } }
}

// Active while the internal test shop is password-protected (dev/trial stores can't disable it).
// Works regardless of store password status, needs a Storefront API public token.
async function fetchProductsViaStorefrontApi(): Promise<FetchResult> {
  const response = await fetch(`https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query: STOREFRONT_QUERY,
      variables: { handle: COLLECTION_HANDLE, first: PRODUCTS_COUNT },
    }),
  })

  if (!response.ok) {
    throw new Error(`Shopify Storefront API error: ${response.status}`)
  }

  const { data, errors } = await response.json()

  if (errors?.length) {
    throw new Error(errors[0].message)
  }

  const collection = data?.collectionByHandle
  const nodes: StorefrontProductNode[] = collection?.products?.nodes ?? []

  return {
    image: collection?.image
      ? {
          url: shopifyImageUrl(collection.image.url, 1320),
          srcset: shopifyImageSrcset(collection.image.url, COLLECTION_IMAGE_WIDTHS),
          alt: collection.image.altText ?? '',
        }
      : null,
    products: nodes.map((product) => {
      const currency = product.priceRange.minVariantPrice.currencyCode
      const min = Number(product.priceRange.minVariantPrice.amount)
      const max = Number(product.priceRange.maxVariantPrice.amount)
      const compareAtMin = Number(product.compareAtPriceRange.minVariantPrice.amount)
      const image = product.featuredImage

      return {
        id: product.id,
        title: product.title,
        url: `https://${SHOP_DOMAIN}/products/${product.handle}`,
        imageUrl: image && shopifyImageUrl(image.url, 600),
        imageSrcset: image && shopifyImageSrcset(image.url, PRODUCT_IMAGE_WIDTHS, image.width),
        imageAlt: image?.altText ?? product.title,
        badge: extractBadge(product.tags),
        ...buildPriceDisplay(min, max, compareAtMin, currency),
      }
    }),
  }
}

type JsonEndpointProduct = {
  title: string
  handle: string
  tags: string[]
  images: { src: string }[]
  variants: { price: string; compare_at_price: string | null }[]
}

// Swap to this once the external agency's shop is live and public: no token, plain public JSON
// endpoint. Same FetchResult shape as fetchProductsViaStorefrontApi, so the Twig markup never
// changes — only the call in init() below needs to switch, plus SHOP_DOMAIN/COLLECTION_HANDLE if
// they differ. Note: the legacy /products.json endpoint doesn't expose the collection's own image,
// so `image` stays null here.
export async function fetchProductsViaProductsJson(): Promise<FetchResult> {
  const response = await fetch(
    `https://${SHOP_DOMAIN}/collections/${COLLECTION_HANDLE}/products.json?limit=${PRODUCTS_COUNT}`
  )

  if (!response.ok) {
    throw new Error(`Shopify products endpoint error: ${response.status}`)
  }

  const { products }: { products: JsonEndpointProduct[] } = await response.json()

  return {
    image: null,
    products: products.map((product) => {
      const prices = product.variants.map((variant) => Number(variant.price))
      const min = Math.min(...prices)
      const max = Math.max(...prices)
      const compareAtValues = product.variants
        .filter((variant) => variant.compare_at_price)
        .map((variant) => Number(variant.compare_at_price))
      const compareAtMin = compareAtValues.length ? Math.min(...compareAtValues) : null

      return {
        id: product.handle,
        title: product.title,
        url: `https://${SHOP_DOMAIN}/products/${product.handle}`,
        imageUrl: product.images[0]?.src ?? null,
        imageSrcset: null,
        imageAlt: product.title,
        badge: extractBadge(product.tags),
        ...buildPriceDisplay(min, max, compareAtMin, 'CHF'),
      }
    }),
  }
}

export default () => ({
  ...Carousel({ align: 'start', loop: false, imageSelector: '[data-product-card-image]' }),
  shopUrl: `https://${SHOP_DOMAIN}`,
  products: [] as Product[],
  collectionImage: null as CollectionImage,
  loading: true,
  error: false,

  async init() {
    try {
      const result = await fetchProductsViaStorefrontApi()
      this.products = result.products
      this.collectionImage = result.image
    } catch (e) {
      this.error = true
      console.error('products-carousel.ts:', e)
    } finally {
      this.loading = false

      if (this.products.length) {
        await (this as any).$nextTick()
        ;(this as any)._initEmbla()
      }
    }
  },
})
